import React, { useState } from 'react';
import { useTasks } from '../context/TasksContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Plus, CheckCircle2, Circle, Trash2, Calendar, AlertTriangle, AlertCircle, Loader2 } from 'lucide-react';

const priorityConfig = {
  urgent: { label: 'Pilne', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', icon: AlertTriangle },
  important: { label: 'Ważne', color: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500', icon: AlertCircle },
  normal: { label: 'Normalne', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500', icon: Circle },
  done: { label: 'Zrobione', color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500', icon: CheckCircle2 }
};

const TasksPanel = () => {
  const { tasks, loading, createTask, updateTaskStatus, deleteTask } = useTasks();
  const { isAdmin } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'normal',
    due_date: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Wprowadź tytuł zadania');
      return;
    }

    setSubmitting(true);
    try {
      await createTask(formData);
      toast.success('Zadanie dodane');
      setFormData({ title: '', description: '', priority: 'normal', due_date: '' });
      setDialogOpen(false);
    } catch (err) {
      toast.error('Błąd dodawania zadania');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'done' ? 'open' : 'done';
    try {
      await updateTaskStatus(taskId, newStatus);
      toast.success(newStatus === 'done' ? 'Zadanie wykonane!' : 'Zadanie otwarte');
    } catch (err) {
      toast.error('Błąd aktualizacji');
    }
  };

  const handleDelete = async (taskId) => {
    try {
      await deleteTask(taskId);
      toast.success('Zadanie usunięte');
    } catch (err) {
      toast.error('Błąd usuwania');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
  };

  const openTasks = tasks.filter(t => t.status !== 'done');
  const doneTasks = tasks.filter(t => t.status === 'done');

  // Sort by priority: urgent > important > normal
  const sortedOpenTasks = openTasks.sort((a, b) => {
    const priorityOrder = { urgent: 0, important: 1, normal: 2 };
    return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
  });

  return (
    <Card data-testid="tasks-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-gold-500" />
            Zadania
            {openTasks.length > 0 && (
              <Badge variant="secondary" className="ml-2 text-xs">
                {openTasks.length}
              </Badge>
            )}
          </CardTitle>
          {isAdmin && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-gold-500 hover:bg-gold-600 text-navy-800 h-8" data-testid="add-task-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Nowe
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Nowe zadanie</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div>
                    <Input
                      placeholder="Tytuł zadania"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="h-10"
                      data-testid="task-title-input"
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="Opis (opcjonalnie)"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      data-testid="task-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Priorytet</label>
                      <Select
                        value={formData.priority}
                        onValueChange={(val) => setFormData({ ...formData, priority: val })}
                      >
                        <SelectTrigger data-testid="task-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="urgent">🔴 Pilne</SelectItem>
                          <SelectItem value="important">🟠 Ważne</SelectItem>
                          <SelectItem value="normal">🔵 Normalne</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Termin</label>
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        className="h-10"
                        data-testid="task-duedate-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gold-500 hover:bg-gold-600 text-navy-800"
                    disabled={submitting}
                    data-testid="task-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Dodaj zadanie
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <CheckCircle2 className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-sm">Brak zadań</p>
            {isAdmin && <p className="text-xs text-slate-400 mt-1">Kliknij "Nowe" aby dodać</p>}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Open tasks */}
            {sortedOpenTasks.length > 0 && (
              <div className="space-y-2">
                {sortedOpenTasks.map((task) => {
                  const config = priorityConfig[task.priority] || priorityConfig.normal;
                  const PriorityIcon = config.icon;
                  return (
                    <div
                      key={task.id}
                      className={`p-3 rounded-lg border ${config.color} transition-all hover:shadow-sm`}
                      data-testid={`task-item-${task.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={task.status === 'done'}
                          onCheckedChange={() => handleToggleStatus(task.id, task.status)}
                          className="mt-0.5"
                          data-testid={`task-checkbox-${task.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${config.dot}`} />
                            <p className="font-medium text-sm truncate">{task.title}</p>
                          </div>
                          {task.description && (
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{task.description}</p>
                          )}
                          {task.due_date && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                              <Calendar className="w-3 h-3" />
                              <span>{formatDate(task.due_date)}</span>
                            </div>
                          )}
                        </div>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-slate-400 hover:text-red-500 hover:bg-red-50"
                            onClick={() => handleDelete(task.id)}
                            data-testid={`task-delete-${task.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Done tasks (collapsed) */}
            {doneTasks.length > 0 && (
              <div className="pt-3 border-t border-slate-200">
                <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                  Wykonane ({doneTasks.length})
                </p>
                <div className="space-y-1">
                  {doneTasks.slice(0, 3).map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-2 p-2 rounded bg-slate-50 text-slate-500"
                    >
                      <Checkbox
                        checked={true}
                        onCheckedChange={() => handleToggleStatus(task.id, task.status)}
                        className="opacity-50"
                      />
                      <span className="text-sm line-through truncate">{task.title}</span>
                    </div>
                  ))}
                  {doneTasks.length > 3 && (
                    <p className="text-xs text-slate-400 text-center py-1">
                      +{doneTasks.length - 3} więcej
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TasksPanel;
